import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { TableProperties, Loader2 } from "lucide-react";

/**
 * Live COLUMN + BEAM schedule. Re-computes (debounced) whenever the edited
 * model changes, so structural marks/sizes stay in sync while the user edits.
 */
export const StructuralSchedule = ({ model, kind = "villa", floors = 1, slabSystem = "solid", designCode = "SBC_304", lang = "ar" }) => {
  const isAr = (lang || "ar").toLowerCase().startsWith("ar");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!model?.walls?.length) return;
    clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(() => {
      api.editorStructuralSchedule({ model, kind, floors, slab_system: slabSystem, design_code: designCode })
        .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    }, 500);
    return () => clearTimeout(timer.current);
  }, [model, kind, floors, slabSystem, designCode]);

  if (!model?.walls?.length) return null;

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-4" data-testid="structural-schedule">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TableProperties className="h-5 w-5 text-primary" />
          <h3 className="font-bold">{isAr ? "جدول الأعمدة والكمرات (حي)" : "Column & beam schedule (live)"}</h3>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
      </div>

      {data && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">{data.code} · {data.materials?.concrete_grade} · {isAr ? "تركيب" : "combo"} {data.loads?.combination} · {data.column_count} {isAr ? "عمود" : "cols"}</p>

          <div>
            <p className="mb-1 text-sm font-bold">{isAr ? "الأعمدة" : "Columns"}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="schedule-columns">
                <thead><tr className="text-muted-foreground">
                  <Th>{isAr ? "علامة" : "Mark"}</Th><Th>{isAr ? "مقاس" : "Size"}</Th><Th>{isAr ? "تسليح" : "Bars"}</Th><Th>{isAr ? "كانات" : "Ties"}</Th><Th>{isAr ? "عدد" : "Qty"}</Th>
                </tr></thead>
                <tbody>
                  {(data.column_schedule || []).map((c, i) => (
                    <tr key={i} className="border-t border-border/50">
                      <Td b>{c.mark}</Td><Td>{c.size} مم</Td><Td>{c.long_bars}</Td><Td>{c.ties}</Td><Td>{c.count}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <p className="mb-1 text-sm font-bold">{isAr ? "الكمرات" : "Beams"}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="schedule-beams">
                <thead><tr className="text-muted-foreground">
                  <Th>{isAr ? "علامة" : "Mark"}</Th><Th>{isAr ? "بحر" : "Span"}</Th><Th>{isAr ? "مقاس" : "Size"}</Th><Th>{isAr ? "سفلي" : "Bottom"}</Th><Th>{isAr ? "كانات" : "Stirrups"}</Th>
                </tr></thead>
                <tbody>
                  {(data.beam_schedule || []).map((b, i) => (
                    <tr key={i} className="border-t border-border/50">
                      <Td b>{b.mark}</Td><Td>{b.span_m} م</Td><Td>{b.size_mm} مم</Td><Td>{b.bottom_bars}</Td><Td>{b.stirrups}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[11px] leading-relaxed text-amber-400/90">{isAr ? data.note_ar : data.note_en}</p>
        </div>
      )}
    </div>
  );
};

const Th = ({ children }) => <th className="px-2 py-1 text-start font-bold">{children}</th>;
const Td = ({ children, b }) => <td className={`px-2 py-1 ${b ? "font-bold text-primary" : "text-muted-foreground"}`}>{children}</td>;

export default StructuralSchedule;
